import ContactPickerModal from 'components/forms/form-components/select-picker/ContactPickerModal';
import TextInput from 'components/forms/inputs/rhf-inputs/TextInput';
import { isValidEmailList } from 'components/forms/inputs/rhf-validators/validators';
import InfoTooltip from 'components/InfoTooltip.tsx';
import ContactDynamicMultiSelectPicker from 'components/party/ContactDynamicMultiSelectPicker';
import ContactDynamicSelectPicker from 'components/party/ContactDynamicSelectPicker';
import { ReactNode, useId } from 'react';
import { useFormContext } from 'react-hook-form';
import {
    useLazyGetMinContactsListWithFiltersQuery,
    useLazyGetMinContactsWithRelationsListWithFiltersQuery,
} from 'store/api/contacts';
import { ContactMinDto, ContactMinDtoWithRelationsDto, PartyRelationshipTypeCode, PartyType } from 'types/contacts';
import { InvoiceData, InvoiceForm, InvoiceStatusType } from 'types/invoices';
import { t } from 'utils/getTranslations.ts';
import { useGetOptionLabelWithPartyNumberAndEmail } from 'utils/partyNameUtil';
import { useHasPermissions } from 'utils/permissionUtil.ts';
import { sortByField } from 'utils/sort.ts';

interface Props {
    invoice: InvoiceData;
    isInViewMode: boolean;
    updateInvoiceLimited: () => void;
    clientParty: ContactMinDto;
    /** In the new design's card, whose section lays its fields two to a row. */
    isInCard?: boolean;
}

const RecipientsCard: React.FC<Props> = ({ isInViewMode, updateInvoiceLimited, clientParty, invoice, isInCard }) => {
    const { control, setValue } = useFormContext<InvoiceForm>();
    const isPostponed = invoice.invoiceStatusTypeCode === InvoiceStatusType.POSTPONED;
    const hasUpdatePermission = useHasPermissions(['UPDATE.INVOICES.ALL']);
    const readonly = (isInViewMode && !hasUpdatePermission) || isPostponed;
    const getOptionLabelWithPartyNumberAndEmail = useGetOptionLabelWithPartyNumberAndEmail();
    const hintId = useId();
    // The card's address fields say how to list several under the field, which it describes.
    const showsEmailHint = !!isInCard && !readonly;
    const withEmailHint = (input: ReactNode, id: string) =>
        showsEmailHint ? (
            <div className="flex min-w-0 flex-col gap-1">
                {input}
                <p id={id} className="text-muted-foreground m-0 text-sm">
                    {t('invoices.general-tab.recipients.email-separator-hint')}
                </p>
            </div>
        ) : (
            input
        );

    const invoiceMatters = sortByField(invoice.invoiceMatters, 'clientParty.name').filter(
        (matter) => matter.clientParty.partyType === PartyType.COMPANY
    );

    const groupContacts = (option: ContactMinDtoWithRelationsDto): string | null => {
        const isEmployee =
            option.relatesTo?.some(
                (rel) =>
                    rel.partyRelationshipTypeCode === PartyRelationshipTypeCode.EMPLOYEE &&
                    rel.otherParty.id === clientParty.id
            ) ?? false;

        if (isEmployee) {
            return clientParty.name;
        }

        const matterClientParty = invoiceMatters.find((matter) =>
            option.relatesTo?.some(
                (rel) =>
                    rel.partyRelationshipTypeCode === PartyRelationshipTypeCode.EMPLOYEE &&
                    rel.otherParty.id === matter.clientParty.id
            )
        );

        if (matterClientParty) {
            return matterClientParty.clientParty.name;
        }

        return t('field.other');
    };

    const invoiceRecipientQueryCompanyIds = [
        ...new Set(
            [
                clientParty.partyType === PartyType.COMPANY ? clientParty.id : undefined,
                ...invoiceMatters.flatMap((matter) => matter.clientParty.id),
            ].filter(Boolean)
        ),
    ];

    return (
        <div className={isInCard ? 'grid gap-x-6 gap-y-2.5 @2xl:grid-cols-2' : 'flex flex-col gap-4'}>
            <ContactDynamicSelectPicker
                control={control}
                setValue={setValue}
                paginated={{
                    fetch: invoiceRecipientQueryCompanyIds.length
                        ? useLazyGetMinContactsWithRelationsListWithFiltersQuery
                        : useLazyGetMinContactsListWithFiltersQuery,
                    filters: `partyType==${PartyType.PERSON}`,
                    searchField: ['name', 'partyNumber'],
                    additionalParams: invoiceRecipientQueryCompanyIds.length
                        ? { prioritizeEmployeesOf: invoiceRecipientQueryCompanyIds }
                        : undefined,
                }}
                {...(invoiceRecipientQueryCompanyIds.length && {
                    groupByStaticFields: groupContacts,
                })}
                isPickerModalOptionEnabled={(option) => option.partyType === PartyType.PERSON}
                PickerModalComponent={ContactPickerModal}
                groupSortOrder={[t('efforts.groups.company-related')]}
                getOptionLabel={getOptionLabelWithPartyNumberAndEmail}
                name="primaryRecipientParty"
                label="invoices.general-tab.recipients.primary-recipient"
                tooltip={<InfoTooltip text="invoices.tooltips.primary-recipient" />}
                readonly={readonly ? { linkTo: 'contact' } : false}
                onChange={updateInvoiceLimited}
            />
            {withEmailHint(
                <TextInput
                    control={control}
                    name="primaryRecipientAdditionalEmails"
                    label="invoices.general-tab.recipients.specific-recipient-address"
                    tooltip={<InfoTooltip text="invoices.tooltips.specific-recipient-address" />}
                    readonly={readonly}
                    onBlur={updateInvoiceLimited}
                    validate={{
                        emailListIsValid: isValidEmailList(t('validation.invalid-email-format')),
                    }}
                    inputProps={showsEmailHint ? { 'aria-describedby': `${hintId}-to` } : undefined}
                />,
                `${hintId}-to`
            )}
            <ContactDynamicMultiSelectPicker
                control={control}
                setValue={setValue}
                paginated={{
                    fetch: invoiceRecipientQueryCompanyIds.length
                        ? useLazyGetMinContactsWithRelationsListWithFiltersQuery
                        : useLazyGetMinContactsListWithFiltersQuery,
                    filters: `partyType==${PartyType.PERSON}`,
                    searchField: ['name', 'partyNumber'],
                    additionalParams: invoiceRecipientQueryCompanyIds.length
                        ? { prioritizeEmployeesOf: invoiceRecipientQueryCompanyIds }
                        : undefined,
                }}
                {...(invoiceRecipientQueryCompanyIds.length && {
                    groupByStaticFields: groupContacts,
                })}
                isPickerModalOptionEnabled={(option) => option.partyType === PartyType.PERSON}
                PickerModalComponent={ContactPickerModal}
                groupSortOrder={[t('efforts.groups.company-related')]}
                getOptionLabel={getOptionLabelWithPartyNumberAndEmail}
                name="secondaryRecipientParties"
                label="invoices.general-tab.recipients.secondary-recipient"
                tooltip={<InfoTooltip text="invoices.tooltips.secondary-recipient" />}
                readonly={readonly ? { linkTo: 'contact' } : false}
                onChange={updateInvoiceLimited}
            />
            {withEmailHint(
                <TextInput
                    control={control}
                    name="secondaryRecipientAdditionalEmails"
                    label="invoices.general-tab.recipients.other-secondary-recipients"
                    tooltip={<InfoTooltip text="invoices.tooltips.other-secondary-recipients" />}
                    readonly={readonly}
                    onBlur={updateInvoiceLimited}
                    inputProps={showsEmailHint ? { 'aria-describedby': `${hintId}-cc` } : undefined}
                />,
                `${hintId}-cc`
            )}
        </div>
    );
};

export default RecipientsCard;
